import { customFieldRegExp } from "hooks/use-available-custom-fields";
import { CustomFields } from ".";
import WhatsappTemplate from "../whatsapp-template";

export enum WhatsAppTemplateStatuses {
  APPROVED = "approved",
  PENDING = "pending",
  REJECTED = "rejected",
  UNKNOWN = "unknown",
}

export enum TemplateType {
  CUSTOM = "custom",
  FUZEY = "fuzey",
}

export enum TemplateCategory {
  UTILITY = "utility",
  MARKETING = "marketing",
}

class FullTemplateDomain {
  public id: string;

  public merchantId: number;

  public name: string;

  public title: string;

  public text: string;

  public purpose: string | null;

  public favourite: boolean;

  public shortcut: string | null;

  public channels: string[];

  public subject: string | null;

  public type: TemplateType;

  public whatsappTemplateStatus: WhatsAppTemplateStatuses | null;

  public customFields: CustomFields;

  public category: TemplateCategory | null;

  public isEditable: boolean;

  public mediaUrl: string | null;

  public mediaType: string | null;

  constructor(
    id: string,
    merchantId: number,
    name: string,
    title: string,
    text: string,
    purpose: string | null,
    favourite: boolean,
    shortcut: string | null,
    channels: string[],
    subject: string | null,
    type: TemplateType,
    whatsappTemplateStatus: WhatsAppTemplateStatuses | null,
    customFields: CustomFields,
    isEditable: boolean,
    category: TemplateCategory | null,
    mediaUrl: string | null,
    mediaType: string | null
  ) {
    this.id = id;
    this.merchantId = merchantId;
    this.name = name;
    this.title = title;
    this.text = text;
    this.purpose = purpose;
    this.favourite = favourite;
    this.shortcut = shortcut;
    this.channels = channels;
    this.subject = subject;
    this.type = type;
    this.whatsappTemplateStatus = whatsappTemplateStatus;
    this.customFields = customFields;
    this.isEditable = isEditable;
    this.category = category;
    this.mediaUrl = mediaUrl;
    this.mediaType = mediaType;
  }

  getWhatsAppStatusColor(): string {
    switch (this.whatsappTemplateStatus) {
      case WhatsAppTemplateStatuses.APPROVED:
        return "green";
      case WhatsAppTemplateStatuses.PENDING:
        return "gray";
      case WhatsAppTemplateStatuses.REJECTED:
        return "red";
      case WhatsAppTemplateStatuses.UNKNOWN:
        return "gray";

      default:
        return "";
    }
  }

  getMedia(): {
    type: string;
    url: string;
  } | null {
    if (this.mediaType && this.mediaUrl) {
      return {
        type: this.mediaType || "",
        url: this.mediaUrl || "",
      };
    }

    return null;
  }

  getWholeText(): string {
    return this.text;
  }

  isWhatsappTemplate(): this is WhatsappTemplate {
    return false;
  }

  isRegularTemplate(): this is FullTemplateDomain {
    return true;
  }

  isGeneric(): boolean {
    return this.type === TemplateType.FUZEY;
  }

  canEdit(): boolean {
    return this.isEditable === true;
  }

  isCustom(): boolean {
    return this.type === TemplateType.CUSTOM;
  }

  private forEachCustomField(
    callback: (startsAt: number, endsAt: number, key: string) => string | void
  ): void {
    const wholeText = this.getWholeText();
    const isMessageEmpty = !wholeText.trim();

    if (isMessageEmpty) {
      return;
    }

    let textToIterate = wholeText;
    let lastMatch;
    const findCustomField = new RegExp(customFieldRegExp, "g");

    while ((lastMatch = findCustomField.exec(textToIterate))) {
      const startsAt = lastMatch.index;
      let endsAt = startsAt + 1;
      const charactersFromText = textToIterate.split("");

      while (endsAt < charactersFromText.length) {
        const letter = charactersFromText[endsAt];

        if (/[\W]/.test(letter)) break;

        endsAt++;
      }

      const result = callback(startsAt, endsAt, lastMatch[1]);

      if (typeof result === "string") {
        textToIterate = result;
      }

      if (!findCustomField.global) break; // To be safe for a moment
    }
  }

  getCustomFieldKeys(): string[] {
    const customFieldKeys: string[] = [];

    this.forEachCustomField((_, __, key) => {
      if (!customFieldKeys.includes(key)) {
        customFieldKeys.push(key);
      }
    });

    return customFieldKeys;
  }
}

export default FullTemplateDomain;
